/**
 * useAppInsightsPageTracking
 *
 * Listens to React Router navigation events and fires an Application Insights
 * page-view telemetry event after each route change.  Mount this hook once,
 * inside <BrowserRouter>, so it has access to the router context.
 *
 * The hook produces a human-readable page name from the matched route pattern
 * (e.g. "/practice-area/:practiceAreaId" → "Practice Area") rather than
 * sending the raw URL, which keeps the App Insights page map clean.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageView } from '@/services/appInsights'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'
import type { GetContentBundleResponse, ApiCustomView } from '@/types/api'

interface ViewContext {
  selectedModelViewId: number | null
  selectedCustomViewId: number | null
  customViews: ApiCustomView[]
}

function resolveViewProps(
  bundle: GetContentBundleResponse | undefined,
  { selectedModelViewId, selectedCustomViewId, customViews }: ViewContext,
): Record<string, string> {
  if (selectedCustomViewId !== null) {
    const cv = customViews.find((v) => v.id === selectedCustomViewId)
    return {
      customViewId: String(selectedCustomViewId),
      ...(cv ? { customViewName: cv.name } : {}),
      ...(cv && cv.isOwner === false ? { customViewShared: 'true' } : {}),
    }
  }
  if (selectedModelViewId !== null) {
    const mv = bundle?.modelViews.find((v) => v.id === selectedModelViewId)
    return { modelViewId: String(selectedModelViewId), ...(mv ? { modelViewName: mv.displayName } : {}) }
  }
  return {}
}

/** Maps a route pathname pattern to a friendly display name. */
function resolvePageName(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'Welcome'
  if (/^\/practice-area\//i.test(pathname)) return 'Practice Area'
  if (/^\/practice\//i.test(pathname)) return 'Practice'
  if (/^\/search/i.test(pathname)) return 'Search Results'
  if (/^\/framework\//i.test(pathname)) return 'Framework'
  if (/^\/load-identity/i.test(pathname)) return 'Load Identity'
  if (/^\/admin\/practice-areas/i.test(pathname)) return 'Admin – Practice Areas'
  if (/^\/admin\/practices/i.test(pathname)) return 'Admin – Practices'
  if (/^\/admin\/levels/i.test(pathname)) return 'Admin – Levels'
  if (/^\/admin\/model-views/i.test(pathname)) return 'Admin – Model Views'
  if (/^\/admin\/glossary/i.test(pathname)) return 'Admin – Glossary'
  if (/^\/admin\/languages/i.test(pathname)) return 'Admin – Languages'
  if (/^\/admin\/content-tags/i.test(pathname)) return 'Admin – Content Tags'
  if (/^\/admin\/contextual-content/i.test(pathname)) return 'Admin – Contextual Content'
  if (/^\/admin\/media/i.test(pathname)) return 'Admin – Media'
  if (/^\/admin\/release-bundles/i.test(pathname)) return 'Admin – Release Bundles'
  if (/^\/admin\/review-queue/i.test(pathname)) return 'Admin – Review Queue'
  if (/^\/admin\/roles/i.test(pathname)) return 'Admin – Roles'
  if (/^\/admin\/stats/i.test(pathname)) return 'Admin – Stats'
  if (/^\/admin\/analytics/i.test(pathname)) return 'Admin – Analytics'
  if (/^\/admin/i.test(pathname)) return 'Admin Dashboard'
  return pathname
}

/** Extracts route parameters and display names as custom dimensions for App Insights. */
function resolveRouteProperties(
  pathname: string,
  bundle: GetContentBundleResponse | undefined,
  viewContext: ViewContext,
): Record<string, string> | undefined {
  const practiceAreaMatch = pathname.match(/^\/practice-area\/([^/?]+)/)
  if (practiceAreaMatch) {
    const id = practiceAreaMatch[1]
    const name = bundle?.practiceAreas.find((pa) => pa.id === Number(id))?.displayName
    return { practiceAreaId: id, ...(name ? { practiceAreaName: name } : {}), ...resolveViewProps(bundle, viewContext) }
  }
  const frameworkMatch = pathname.match(/^\/framework\/([^/?]+)/)
  if (frameworkMatch) {
    const id = frameworkMatch[1]
    const name = bundle?.externalFrameworks.find((f) => f.id === Number(id))?.name
    return { frameworkId: id, ...(name ? { frameworkName: name } : {}) }
  }
  const practiceMatch = pathname.match(/^\/practice\/([^/?]+)/)
  if (practiceMatch) {
    const id = practiceMatch[1]
    const name = bundle?.practices.find((p) => p.id === Number(id))?.displayName
    return { practiceId: id, ...(name ? { practiceName: name } : {}), ...resolveViewProps(bundle, viewContext) }
  }
  return undefined
}

export function useAppInsightsPageTracking(isTourActive = false): void {
  const location = useLocation()
  const { data: bundle } = useContentBundle()
  const { isManualOffline, isTrulyOffline, offlineSessionId, selectedModelViewId, selectedCustomViewId, customViews } = useAppStore()
  // Refs keep the latest values available inside the navigation effect without
  // making them dependencies (which would fire duplicate page views).
  const bundleRef = useRef(bundle)
  bundleRef.current = bundle
  const isTourActiveRef = useRef(isTourActive)
  isTourActiveRef.current = isTourActive
  const offlineRef = useRef({ isManualOffline, isTrulyOffline, offlineSessionId })
  offlineRef.current = { isManualOffline, isTrulyOffline, offlineSessionId }
  const viewContextRef = useRef<ViewContext>({ selectedModelViewId, selectedCustomViewId, customViews })
  viewContextRef.current = { selectedModelViewId, selectedCustomViewId, customViews }

  useEffect(() => {
    const pageName = resolvePageName(location.pathname)
    const routeProps = resolveRouteProperties(location.pathname, bundleRef.current, viewContextRef.current)
    const { isManualOffline: manual, isTrulyOffline: network, offlineSessionId: sessionId } = offlineRef.current
    const offlineMode = manual && network ? 'both' : manual ? 'manual' : network ? 'network' : 'none'
    const isOffline = manual || network
    const properties = {
      ...routeProps,
      ...(isTourActiveRef.current ? { tourNavigation: 'true' } : {}),
      offlineMode,
      ...(isOffline && sessionId ? { offlineSessionId: sessionId } : {}),
    }
    trackPageView(pageName, location.pathname + location.search, properties)
  }, [location.pathname, location.search])
}
